/*
 * -----------------------------------------------------------------------
 *  <copyright file="assign-indications.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@progress/kendo-react-inputs';

import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import { useTranslation, useBoolean } from 'hooks';
import { OrganisationIndicationFields, OrganisationProgramFields } from 'queries/generated/fragments.generated';
import { SAVE_ORGANISATION_PROGRAM } from 'queries/org-admin';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import Filter from 'views/components/core/filter';
import ItemList from 'views/components/core/item-list';

const AssignIndications = ({
    onClose,
    open,
    indications,
    currentProgram,
}: {
    onClose: Function;
    open: boolean;
    indications: OrganisationIndicationFields[];
    currentProgram: OrganisationProgramFields;
}) => {
    const [saveProgram] = useMutation(SAVE_ORGANISATION_PROGRAM);

    const { t } = useTranslation();
    const [selectedVariables, setSelectedVariables] = useState([]);
    const [filter, setFilter] = useState('');
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [program, setProgram] = useState(null);
    const saving = useBoolean(false);
    const showAssignedOnly = useBoolean(false);
    const [allRecords, setAllRecords] = useState([]);
    const disabledSaveButton = useBoolean(true);

    useEffect(() => {
        if (open && program) {
            const value = getAvailableIndications();
            setFilteredRecords(value.filteredIndications);
            setAllRecords(value.filteredIndications);
        }
    }, [open, indications, filter, program]);

    const setSelectedItemsByDefault = () => {
        const selectedByDefault: { id: number }[] = [];
        currentProgram?.indicationIds.map((indicationId: any) => {
            selectedByDefault.push(indications.find((indication: any) => indication.id === indicationId));
        });
        setSelectedVariables(selectedByDefault);
    };

    useEffect(() => {
        if (open && currentProgram) setProgram(currentProgram);
        setSelectedItemsByDefault();
    }, [currentProgram, open]);

    useEffect(() => {
        setFilteredRecords(showAssignedOnly.value ? selectedVariables : allRecords);
    }, [showAssignedOnly]);

    const getAvailableIndications = (): { filteredIndications: any[]; allIndicationLength: number } => ({
        filteredIndications: indications.filter((item: any) =>
            !filter.length ? true : item.name.toUpperCase().indexOf(filter) >= 0,
        ),
        allIndicationLength: indications.length,
    });

    const close = (action?: any) => {
        showAssignedOnly.setFalse();
        setSelectedVariables([]);
        disabledSaveButton.setTrue();
        onClose(action);
    };

    const assignItems = (closeModal: boolean) => {
        let ids = [];
        ids = selectedVariables.map((r: any) => r.id);
        ids = [...Array.from(new Set(ids))];
        saving.setTrue();
        disabledSaveButton.setTrue();

        saveProgram({
            variables: {
                program: {
                    id: program?.id ?? null,
                    name: program?.name,
                    indicationIds: ids,
                    productIds: program?.productIds ?? [],
                },
            },
            refetchQueries: [
                {
                    query: GET_ORGANISATION_PROGRAM_PRODUCT,
                },
            ],
        })
            .then((res: any) => {
                setProgram({
                    ...program,
                    indicationIds: res.data.saveOrganisationProgram.indicationIds,
                    productIds: res.data.saveOrganisationProgram.productIds,
                });
                close({
                    amount: selectedVariables.length,
                    success: true,
                    close: closeModal,
                });
                setSelectedVariables([]);
                saving.setFalse();
                disabledSaveButton.setTrue();
            })
            .catch(() => {
                close('error');
                disabledSaveButton.setFalse();
            });
    };

    return (
        <CustomDialog
            onClose={close}
            aria-labelledby="assign-indication"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
            slotProps={{
                transition: {
                    onEntering: () => {
                        // onEntering callback is called while rendering/entering dialog
                        // check if there are indications to associate
                        // if not then open new indication dialog
                        if (!getAvailableIndications().filteredIndications?.length && open) close('open-add');
                    },
                },
            }}
        >
            <DialogTitle id="assign-critical-variables-modal-title">
                {t('admin:assignIndications')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent
                className={`${
                    program?.studiesAmount ? 'add-risk-dialog-content warning pb-0' : 'add-risk-dialog-content pb-0'
                }`}
            >
                <>
                    {program?.studiesAmount ? (
                        <div className="wrap-warning-message-absolute">
                            <WarningForDialog firstNumber={program?.studiesAmount} type="product" />
                        </div>
                    ) : null}
                    {program && (
                        <div className="content-text">
                            <span className="title">{t('admin:program')}:</span>
                            {program.name}
                        </div>
                    )}
                    <div className="filter-item-mb">
                        <Filter
                            id="assign-critical-variables-dialog-filter"
                            onChange={({ text }: any) => setFilter(text)}
                        />
                    </div>
                    <div className="list-info">
                        <p className="text-length">
                            {filteredRecords?.length} {t('admin:indicationRecords')}
                        </p>
                        <Checkbox
                            rounded="none"
                            className="cell-checkbox no-full-width no-border"
                            checked={showAssignedOnly.value}
                            onChange={() => {
                                showAssignedOnly.setValue(!showAssignedOnly.value);
                            }}
                            label={t('admin:assigned')}
                            data-testid="assigned-checkbox"
                            size="medium"
                        />
                    </div>
                    <ItemList
                        id="add-risk-list"
                        items={filteredRecords}
                        textField="name"
                        filter={filter}
                        multiselect
                        onSelectionChange={(value: any) => {
                            disabledSaveButton.setFalse();
                            setSelectedVariables(value);
                        }}
                        selectedItems={selectedVariables}
                    />
                </>
            </DialogContent>

            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                <div className="selected-items-container mb-20">
                    <p>
                        {selectedVariables.length}/{filteredRecords?.length}
                    </p>
                </div>
                <Button
                    text="new"
                    id="assign-risk-new-cv"
                    disabled={saving.value}
                    onClick={() => {
                        close('open-add');
                    }}
                />
                <Button
                    text="update"
                    disabled={disabledSaveButton.value || saving.value}
                    id="assign-risk-save-close"
                    onClick={() => assignItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignIndications;
